import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

interface UseAppKeyboardShortcutsOptions {
  openGlobalSearch: () => void;
  openShortcutsHelp: () => void;
  canCreateWorksite: boolean;
}

/**
 * R19.3: Ctrl/Cmd+K → ricerca globale (anche da input).
 * ?, N, R solo quando il focus non è su campo testo.
 */
export function useAppKeyboardShortcuts({
  openGlobalSearch,
  openShortcutsHelp,
  canCreateWorksite,
}: UseAppKeyboardShortcutsOptions): void {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openGlobalSearch();
        return;
      }

      if (isTypingTarget(e.target)) {
        return;
      }

      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        openShortcutsHelp();
        return;
      }

      if ((e.key === 'n' || e.key === 'N') && canCreateWorksite) {
        e.preventDefault();
        navigate('/app/worksites/new');
        return;
      }

      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        navigate('/app/worksites');
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, openGlobalSearch, openShortcutsHelp, canCreateWorksite]);
}
